import { useTranslation } from 'react-i18next';
import { Typography } from 'app/components';
import Spinner from 'shared/feedback/Spinner';
import { useDocumentTaxonomyConfig, usePresentationTree } from 'entities/tag';
import { CategoryOverridesSection } from './CategoryOverridesSection';

interface CategoriesProps {
  documentId: string;
}

export const Categories = ({ documentId }: CategoriesProps) => {
  const { t } = useTranslation('tag');

  const documentSuperId = documentId ? parseInt(documentId, 10) : undefined;
  const { data: config, isLoading: isConfigLoading } = useDocumentTaxonomyConfig(documentSuperId);
  const { data: presentation, isLoading: isPresentationLoading } = usePresentationTree(documentSuperId);

  if (isConfigLoading || isPresentationLoading) {
    return (
      <div className="tagging-settings">
        <div className="text-center py-4">
          <Spinner />
          <Typography variant="body-7">{t('taxonomy.settings.loading')}</Typography>
        </div>
      </div>
    );
  }

  if (!config) {
    return (
      <div className="tagging-settings">
        <Typography variant="body-7">{t('categories.noConfig') || 'No taxonomy configuration on this document yet.'}</Typography>
      </div>
    );
  }

  const activeTaxonomyId = config.taxonomySelections?.find((t) => t.isActive)?.taxonomyId;
  const networks = presentation?.presentationTree?.networks ?? [];

  if (!activeTaxonomyId) {
    return (
      <div className="tagging-settings">
        <Typography variant="body-7">{t('categories.noActiveTaxonomy') || 'No active taxonomy selected.'}</Typography>
      </div>
    );
  }

  if (networks.length === 0) {
    return (
      <div className="tagging-settings">
        <Typography variant="body-7">{t('categories.noCategories') || 'The active taxonomy exposes no top-level categories.'}</Typography>
      </div>
    );
  }

  return (
    <div className="tagging-settings">
      <CategoryOverridesSection
        configId={config.id}
        activeTaxonomyId={activeTaxonomyId}
        networks={networks}
        overrides={config.taxonomyOverrides ?? {}}
        canEdit={true}
        documentSuperId={documentSuperId}
      />
    </div>
  );
};
